import { parseProblemDetails } from './problem-details.js';
import {
  CORRELATION_ID_HEADER,
  HttpError,
  PLACE_ID_HEADER,
  type HttpClientContext,
  type HttpMethod,
  type RequestOptions,
} from './types.js';

import { env } from '@/shared/config/env.js';
import {
  emitMutationFeedback,
  mutationFeedbackKind,
  shouldEmitMutationError,
} from '@/shared/feedback/mutation-feedback.js';
import { isUserCommitTurn } from '@/shared/feedback/user-commit-turn.js';

const API_PREFIX = '/api';

/**
 * iOS Safari throws on `crypto.randomUUID()` outside a secure context.
 * The customer portal is opened over http:// on the LAN, so POST must not
 * depend on that API. The server replaces a non-ULID with its own id.
 */
export function createCorrelationId(): string {
  try {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Insecure context: fall through.
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const byte6 = bytes[6] ?? 0;
  const byte8 = bytes[8] ?? 0;
  bytes[6] = (byte6 & 0x0f) | 0x40;
  bytes[8] = (byte8 & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function buildUrlForTest(path: string, baseUrl = env.VITE_API_BASE_URL): string {
  const base = baseUrl.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (base) {
    // Mesma regra do proxy do Vite em dev (vite.config.ts `rewrite`) — chamadas usam `/api/...`
    // por convenção interna; com base absoluta (prod) o `/api` é removido antes de compor a URL.
    const withoutPrefix = normalizedPath.startsWith(API_PREFIX)
      ? normalizedPath.slice(API_PREFIX.length) || '/'
      : normalizedPath;
    return `${base}${withoutPrefix}`;
  }

  const withPrefix = normalizedPath.startsWith(API_PREFIX)
    ? normalizedPath
    : `${API_PREFIX}${normalizedPath}`;

  return withPrefix;
}

function buildUrl(path: string): string {
  return buildUrlForTest(path);
}

async function parseJsonResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.includes('json')) {
    return null;
  }

  return response.json() as Promise<unknown>;
}

export class HttpClient {
  constructor(private readonly context: HttpClientContext) {}

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  async getBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const userInitiated = isUserCommitTurn();
    const headers: Record<string, string> = {
      Accept: '*/*',
      ...options.headers,
    };

    if (!options.skipAuth) {
      const token = this.context.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    if (!options.skipPlace) {
      const placeId = this.context.getActivePlaceId();
      if (placeId) {
        headers[PLACE_ID_HEADER] = placeId;
      }
    }

    const response = await fetch(buildUrl(path), {
      method: 'GET',
      headers,
      credentials: 'include',
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });

    if (!response.ok) {
      const payload = await parseJsonResponse(response);
      const problem = parseProblemDetails(payload, response.status);
      if (
        shouldEmitMutationError(
          response.status,
          mutationFeedbackKind('GET', options, userInitiated),
          userInitiated,
          options,
        )
      ) {
        emitMutationFeedback({
          kind: 'error',
          status: response.status,
          code: problem.code,
          message: problem.message,
        });
      }
      throw new HttpError({
        status: response.status,
        code: problem.code,
        message: problem.message,
      });
    }

    return response.blob();
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  /** Multipart POST — do not set Content-Type (browser sets boundary). */
  postFormData<T>(path: string, body: FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, { ...options, formData: true });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, body, options);
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const userInitiated = options._userInitiated ?? isUserCommitTurn();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...options.headers,
    };

    if (body !== undefined && options.formData !== true) {
      headers['Content-Type'] = 'application/json';
    }

    if (!options.skipAuth) {
      const token = this.context.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    if (!options.skipPlace) {
      const placeId = this.context.getActivePlaceId();
      if (placeId) {
        headers[PLACE_ID_HEADER] = placeId;
      }
    }

    if (method !== 'GET' && !headers[CORRELATION_ID_HEADER]) {
      headers[CORRELATION_ID_HEADER] = createCorrelationId();
    }

    const init: RequestInit = {
      method,
      headers,
      credentials: 'include',
    };

    if (body !== undefined) {
      init.body = options.formData === true ? (body as FormData) : JSON.stringify(body);
    }

    if (options.signal !== undefined) {
      init.signal = options.signal;
    }

    const response = await fetch(buildUrl(path), init);

    if (response.status === 401 && !options.skipAuth && options._retryOnUnauthorized !== false) {
      const recovered = await this.context.onUnauthorized();
      if (recovered) {
        return this.request<T>(method, path, body, {
          ...options,
          _retryOnUnauthorized: false,
          _userInitiated: userInitiated,
        });
      }
    }

    if (!response.ok) {
      const payload = await parseJsonResponse(response);
      const problem = parseProblemDetails(payload, response.status);
      const errorOptions: ConstructorParameters<typeof HttpError>[0] = {
        status: response.status,
        code: problem.code,
        message: problem.message,
      };

      if (problem.correlationId !== undefined) {
        errorOptions.correlationId = problem.correlationId;
      }

      if (problem.fieldErrors !== undefined) {
        errorOptions.fieldErrors = problem.fieldErrors;
      }

      if (problem.details !== undefined) {
        errorOptions.details = problem.details;
      }

      const feedbackKind = mutationFeedbackKind(method, options, userInitiated);
      if (shouldEmitMutationError(response.status, feedbackKind, userInitiated, options)) {
        emitMutationFeedback({
          kind: 'error',
          status: response.status,
          code: errorOptions.code,
          message: errorOptions.message,
        });
      }

      throw new HttpError(errorOptions);
    }

    const feedbackKind = mutationFeedbackKind(method, options, userInitiated);
    if (feedbackKind !== null) {
      emitMutationFeedback({ kind: feedbackKind });
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const payload = await parseJsonResponse(response);
    return payload as T;
  }
}

let sharedClient: HttpClient | null = null;

export function createHttpClient(context: HttpClientContext): HttpClient {
  sharedClient = new HttpClient(context);
  return sharedClient;
}

export function getHttpClient(): HttpClient {
  if (!sharedClient) {
    throw new Error('HttpClient not initialized — mount AuthProvider first');
  }

  return sharedClient;
}
